import * as T from '@/core/configs/types'
import { Goods } from './goods'
import { Pallet } from './pallet'
import { StackTypes, SlotStatus, TypeStack } from '@/core/models/constant'

export const StackSlot = {
  no: {
    type: T.String,
    label: 'No.',
    key: 'no'
  },
  rowNo: {
    type: T.String,
    label: 'Row No.',
    key: 'rowNo'
  },
  stackNo: {
    type: T.String,
    label: 'Stack No.',
    key: 'stackNo'
  },
  sectionNo: {
    type: T.String,
    label: 'Section No.',
    key: 'sectionNo'
  },
  slotNo: {
    type: T.String,
    label: 'Slot No.',
    key: 'slotNo'
  },
  customer: {
    type: T.String,
    label: 'Customer',
    key: 'customer'
  },
  palletNo: {
    type: T.String,
    label: 'Pallet No.',
    key: 'palletNo'
  },
  palletCnt: {
    type: T.Number,
    label: 'Pallet CNT.',
    key: 'palletCnt'
  }
}

export const StackSection = {
  sectionNo: {
    type: T.String,
    label: 'Section No.',
    key: 'sectionNo'
  },
  loadRate: {
    type: T.Progress,
    label: 'Load Rate',
    key: 'loadRate'
  }
}

export const StackStats = {
  stacksTotal: {
    type: T.String,
    label: 'Stacks Total',
    key: 'stacksTotal'
  },
  palletsTotal: {
    type: T.String,
    label: 'Pallets Total',
    key: 'palletsTotal'
  },
  slotsTotal: {
    type: T.String,
    label: 'Slots Total',
    key: 'slotsTotal'
  },
  loadRate: {
    type: T.String,
    label: 'Load Rate',
    key: 'loadRate'
  }
}

const StackStatsInfoPicks = [
  { ...StackStats.stacksTotal, type: T.Text, span: 1 },
  { ...StackStats.palletsTotal, type: T.Text, span: 1 },
  { ...StackStats.slotsTotal, type: T.Text, span: 1 },
  { ...StackStats.loadRate, type: T.Text, span: 1 }
]

export const StackStatsInfoWrapUp = T.generateWrapUp(StackStatsInfoPicks)

export const StackSearchForm = Object.fromEntries(
  [
    {
      ...StackSlot.no,
      type: T.TextInput,
      example: 'R01A01A',
      default: '',
      value: '',
      validators: [],
      convertor: value => value
    },
    {
      ...StackSlot.customer,
      type: T.TextInput,
      example: 'Anker',
      default: '',
      value: '',
      validators: [],
      convertor: value => value
    },
    {
      ...Goods.sku,
      type: T.TextInput,
      example: 'CD123456789',
      default: '',
      value: '',
      validators: [],
      convertor: value => value
    },
    {
      ...Pallet.no,
      key: 'palletNo',
      type: T.TextInput,
      example: 'PALLET-12345',
      default: '',
      value: '',
      validators: [],
      convertor: value => value
    }
  ].map(one => [one.key, one])
)

export const StackSlotTableHeaders = [
  StackSlot.rowNo,
  StackSlot.stackNo,
  StackSlot.sectionNo,
  StackSlot.slotNo,
  {
    ...StackSlot.palletCnt,
    width: 150
  }
]

export const StackSectionTableHeaders = [StackSection.sectionNo]

export const StackSlotWithPalletTableHeaders = [
  StackSlot.slotNo,
  StackSlot.palletCnt
]

export const StackSearchParamsWrapUp = searchForm => {
  const params = {}
  Object.keys(searchForm).forEach(key => {
    if (key === 'no') {
      params.slotFuzzy = searchForm[key].value
    } else {
      params[key] = searchForm[key].value
    }
  })
  return params
}

export const Stack = {
  id: {
    type: T.String,
    label: 'Stack ID',
    key: 'id'
  },
  no: {
    type: T.String,
    label: 'Stack No',
    key: 'no'
  },
  type: {
    type: T.Enum,
    label: 'Stack Type',
    key: 'type',
    options: Object.values(StackTypes)
  },
  sectionNum: {
    type: T.Number,
    label: 'Section Num',
    key: 'sectionNum'
  },
  slotPerSection: {
    type: T.Number,
    label: 'Slot per Section',
    key: 'slotPerSection'
  },
  createdTime: {
    type: T.Time,
    label: 'Created on',
    key: 'createdTime'
  },
  updatedTime: {
    type: T.Time,
    label: 'Updated on',
    key: 'updatedTime'
  },
  metaData: {
    type: T.JSON,
    label: 'Metadata',
    key: 'metaData'
  }
}

export const Slot = {
  id: {
    type: T.String,
    label: 'Slot ID',
    key: 'id'
  },
  no: {
    type: T.String,
    label: 'Slot No',
    key: 'no'
  },
  stackId: {
    type: T.String,
    label: 'Stack ID',
    key: 'stackId'
  },
  customer: {
    type: T.String,
    label: 'Customer',
    key: 'customer'
  },
  status: {
    type: T.String,
    label: 'Slot Status',
    key: 'status',
    options: Object.values(SlotStatus)
  },
  createdTime: {
    type: T.Time,
    label: 'Created on',
    key: 'createdTime'
  },
  updatedTime: {
    type: T.Time,
    label: 'Updated on',
    key: 'updatedTime'
  },
  metaData: {
    type: T.JSON,
    label: 'Metadata',
    key: 'metaData'
  }
}
export const StackForm = Object.fromEntries(
  [
    {
      ...Stack.no,
      span: 1,
      type: T.TextInput,
      example: 'R01A',
      default: 'R01A',
      value: 'R01A',
      validators: [T.RequiredValidator]
    },
    {
      ...Stack.type,
      span: 1,
      type: T.Select,
      example: TypeStack,
      default: TypeStack,
      value: TypeStack,
      validators: [T.RequiredValidator]
    },
    {
      ...Stack.sectionNum,
      span: 1,
      type: T.NumberInput,
      example: 1,
      default: 1,
      value: 1,
      validators: [T.RequiredValidator, T.PositiveValidator]
    },
    {
      ...Stack.slotPerSection,
      span: 1,
      type: T.NumberInput,
      example: 1,
      default: 1,
      value: 1,
      validators: [T.RequiredValidator, T.PositiveValidator]
    }
  ].map(one => [one.key, one])
)

export const SlotForm = [
  {
    ...Slot.customer,
    type: T.TextInput
  }
]

const StackInfoPicks = [
  { ...StackForm.no, type: T.Text, span: 1 },
  { ...StackForm.type, type: T.Tag, effect: 'dark', span: 1 },
  { ...StackForm.sectionNum, type: T.Text, span: 1 },
  { ...StackForm.slotPerSection, type: T.Text, span: 1 },
  { ...Stack.createdTime, type: T.Time, span: 1 },
  { ...Stack.updatedTime, type: T.Time, span: 1 }
]

export const StackInfoWrapUp = T.generateWrapUp(StackInfoPicks)
